import { useInfiniteQuery } from '@tanstack/react-query';
import { CircleAlert, ScrollText, X } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Button } from '../components/Button';
import { Drawer } from '../components/Drawer';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import {
  actionLabels,
  auditKey,
  bySystem,
  listAudit,
  metadataLabels,
  reasonLabels,
  type AuditEntry,
  type AuditFilter,
  type Period,
} from '../features/audit/api';
import { useTimeFormat } from '../lib/useTimeFormat';

const periods: { value: Period; label: string }[] = [
  { value: '24h', label: 'Últimas 24 horas' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '', label: 'Todo o período' },
];

function readFilter(p: URLSearchParams): AuditFilter {
  const outcome = p.get('outcome');
  const period = p.get('period');
  return {
    action: p.get('action') ?? '',
    outcome: outcome === 'success' || outcome === 'failure' ? outcome : '',
    period: period === '24h' || period === '30d' || period === '' ? period : '7d',
    actor: p.get('actor') ?? '',
  };
}

const selectClass =
  'h-8 rounded-md border border-line-strong bg-canvas px-2 text-[13px] text-fg hover:border-fg-subtle/60 focus:border-accent';

export function AuditPage() {
  const { request } = useAuth();
  const fmt = useTimeFormat();
  const [params, setParams] = useSearchParams();
  const filter = readFilter(params);
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [actorName, setActorName] = useState<string | null>(null);

  const update = (patch: Partial<Record<keyof AuditFilter, string>>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          // An empty period is meaningful ("all time"), so it is kept explicitly.
          if (v || k === 'period') next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  };

  const audit = useInfiniteQuery({
    queryKey: [...auditKey, filter],
    queryFn: ({ pageParam }) => listAudit(request, filter, pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const entries = audit.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(filter.action || filter.outcome || filter.actor);

  return (
    <>
      <PageHeader
        title="Auditoria"
        description={`Acessos e alterações administrativas da organização. Horários em ${fmt.timeZone ?? 'fuso local'}.`}
      />
      <div className="space-y-3 p-4 sm:p-6">
        <div role="search" className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Período"
            value={filter.period}
            onChange={(e) => {
              update({ period: e.target.value });
            }}
            className={selectClass}
          >
            {periods.map((p) => (
              <option key={p.value || 'all'} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por evento"
            value={filter.action}
            onChange={(e) => {
              update({ action: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os eventos</option>
            {Object.entries(actionLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por resultado"
            value={filter.outcome}
            onChange={(e) => {
              update({ outcome: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os resultados</option>
            <option value="success">Sucesso</option>
            <option value="failure">Falha</option>
          </select>
          {filter.actor && (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong bg-surface-raised pr-1 pl-2.5 text-[13px] text-fg">
              Autor: {actorName ?? 'selecionado'}
              <button
                type="button"
                aria-label="Remover filtro de autor"
                onClick={() => {
                  setActorName(null);
                  update({ actor: '' });
                }}
                className="grid size-6 place-items-center rounded text-fg-subtle hover:bg-surface-hover hover:text-fg"
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            </span>
          )}
          {filtered && (
            <Button
              variant="ghost"
              onClick={() => {
                setActorName(null);
                update({ action: '', outcome: '', actor: '' });
              }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {audit.isError && !audit.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar a auditoria"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void audit.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : audit.data && entries.length === 0 ? (
            <EmptyState
              icon={ScrollText}
              title="Nenhum evento no período"
              description={
                filtered
                  ? 'Nenhum evento corresponde aos filtros aplicados.'
                  : 'Amplie o período para ver eventos mais antigos.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[760px] text-left text-[13px]"
                aria-busy={audit.isFetching}
              >
                <caption className="sr-only">
                  Eventos de auditoria, do mais recente ao mais antigo
                </caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Quando
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Evento
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Resultado
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Autor
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Origem
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Detalhes</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!audit.data
                    ? Array.from({ length: 8 }, (_, i) => <SkeletonRow key={i} />)
                    : entries.map((e) => (
                        <tr
                          key={e.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap text-fg-muted tabular-nums">
                            <time dateTime={e.occurred_at}>{fmt.precise(e.occurred_at)}</time>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="text-fg">{actionLabels[e.action] ?? e.action}</div>
                            {e.tenant && (
                              <div className="text-xs text-warn">em {e.tenant.name}</div>
                            )}
                            {typeof e.metadata.reason === 'string' && (
                              <div className="text-xs text-fg-subtle">
                                {reasonLabels[e.metadata.reason] ?? e.metadata.reason}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={e.outcome === 'success' ? 'ok' : 'crit'}>
                              {e.outcome === 'success' ? 'Sucesso' : 'Falha'}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5">
                            {e.actor ? (
                              <div className="min-w-0">
                                <div className="truncate text-fg">{e.actor.display_name}</div>
                                <div className="truncate text-xs text-fg-subtle">
                                  {e.actor.email}
                                </div>
                              </div>
                            ) : (
                              <span className="text-fg-subtle">
                                {bySystem(e)
                                  ? 'Sistema'
                                  : typeof e.metadata.email === 'string'
                                    ? e.metadata.email
                                    : '—'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                            {e.client_ip ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="ghost"
                              className="h-7"
                              aria-label={`Detalhes de ${actionLabels[e.action] ?? e.action} em ${fmt.precise(e.occurred_at)}`}
                              onClick={() => {
                                setSelected(e);
                              }}
                            >
                              Detalhes
                            </Button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
          {audit.data && entries.length > 0 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-xs text-fg-muted">
              <span aria-live="polite">{entries.length} eventos carregados</span>
              {audit.hasNextPage ? (
                <Button
                  onClick={() => void audit.fetchNextPage()}
                  disabled={audit.isFetchingNextPage}
                  className="h-7"
                >
                  {audit.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                </Button>
              ) : (
                <span>Fim do registro para este filtro</span>
              )}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <AuditDetail
          entry={selected}
          onClose={() => {
            setSelected(null);
          }}
          onFilterActor={(id, name) => {
            setSelected(null);
            setActorName(name);
            update({ actor: id });
          }}
        />
      )}
    </>
  );
}

function AuditDetail({
  entry: e,
  onClose,
  onFilterActor,
}: {
  entry: AuditEntry;
  onClose: () => void;
  onFilterActor: (id: string, name: string) => void;
}) {
  const fmt = useTimeFormat();
  const rows: [string, string][] = [
    ['Quando', `${fmt.precise(e.occurred_at)} (${fmt.timeZone ?? 'local'})`],
    ['UTC', e.occurred_at],
    ['Evento', `${actionLabels[e.action] ?? e.action} · ${e.action}`],
    ...(e.tenant
      ? [['Empresa', `${e.tenant.name} (como Super administrador)`] as [string, string]]
      : []),
    ['Resultado', e.outcome === 'success' ? 'Sucesso' : 'Falha'],
    [
      'Autor',
      e.actor ? `${e.actor.display_name} <${e.actor.email}>` : bySystem(e) ? 'Sistema' : '—',
    ],
    ['Endereço IP', e.client_ip ?? '—'],
    ['Navegador', e.user_agent ?? '—'],
  ];
  const meta = Object.entries(e.metadata);

  return (
    <Drawer
      title="Detalhes do evento"
      description={actionLabels[e.action] ?? e.action}
      onClose={onClose}
    >
      <dl className="space-y-3 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs text-fg-subtle">{k}</dt>
            <dd className="mt-0.5 break-words text-fg">{v}</dd>
          </div>
        ))}
      </dl>
      {e.actor && (
        <Button
          className="mt-4"
          onClick={() => {
            if (e.actor) onFilterActor(e.actor.id, e.actor.display_name);
          }}
        >
          Ver eventos deste autor
        </Button>
      )}
      <h3 className="mt-6 border-t border-line pt-5 text-[13px] font-medium text-fg">
        Dados do evento
      </h3>
      {meta.length === 0 ? (
        <p className="mt-2 text-xs text-fg-subtle">Nenhum dado adicional.</p>
      ) : (
        <dl className="mt-3 space-y-3 text-[13px]">
          {meta.map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-fg-subtle">
                {metadataLabels[k] ?? k} <span className="font-mono">({k})</span>
              </dt>
              <dd className="mt-0.5 font-mono text-xs break-words text-fg">{renderValue(k, v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Drawer>
  );
}

function renderValue(key: string, v: unknown): string {
  if (key === 'reason' && typeof v === 'string') return `${reasonLabels[v] ?? v} (${v})`;
  if (Array.isArray(v)) return v.map(String).join(', ') || '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function SkeletonRow() {
  return (
    <tr aria-hidden="true" className="border-b border-line last:border-0">
      {['w-32', 'w-28', 'w-16', 'w-36', 'w-24', 'w-12'].map((w, i) => (
        <td key={i} className="px-4 py-3.5">
          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
        </td>
      ))}
    </tr>
  );
}
